/**
 * "Notifications" hover submenu for Space, category, and channel context menus.
 *
 * Must render inside an Ark `Menu.Content`; it nests its own `Menu.Root` so the
 * parent menu's `onSelect` never sees these values.
 */

import { Menu, Portal } from '@ark-ui/react';
import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SPACE_NOTIFY_LEVELS,
  SPACE_NOTIFY_TOGGLES,
  categoryAncestorPath,
  isSpaceNotifyLevel,
  type ResolvedSpaceNotifyLevel,
  type SpaceNotificationOverrides,
  type SpaceNotificationOverridesPatch,
  type SpaceNotifyLevel,
  type SpaceNotifyToggle,
} from '@adieuu/shared';
import { Icon } from '../icons/Icon';
import { useSpaceNotificationSettings } from '../hooks/useSpaceNotificationSettings';

const INHERIT = 'inherit';
const RESET = 'notify-reset';
const TOGGLE_ITEM_VALUES: Record<SpaceNotifyToggle, string> = {
  announcements: 'notify-announcements',
  spaceChanges: 'notify-space-changes',
};
/** Grace period for the pointer to travel between the trigger and the submenu. */
export const SUBMENU_HOVER_CLOSE_DELAY_MS = 150;

/**
 * Hover-to-open state for a nested Ark menu. Hosting menus differ, so siblings
 * cannot close it; leaving both the trigger and the submenu does instead.
 * Touch has no hover, so a tap opens it through Ark's own trigger handling.
 */
function useHoverSubmenu() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  useEffect(() => cancelClose, [cancelClose]);

  const onPointerEnter = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      cancelClose();
      setOpen(true);
    },
    [cancelClose],
  );

  const onPointerLeave = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      cancelClose();
      closeTimer.current = setTimeout(() => {
        closeTimer.current = null;
        setOpen(false);
      }, SUBMENU_HOVER_CLOSE_DELAY_MS);
    },
    [cancelClose],
  );

  const onOpenChange = useCallback(
    (details: { open: boolean }) => {
      cancelClose();
      setOpen(details.open);
    },
    [cancelClose],
  );

  return { open, onOpenChange, hoverProps: { onPointerEnter, onPointerMove: onPointerEnter, onPointerLeave } };
}

export interface NotificationSettingsSubmenuProps {
  scope: 'space' | 'category' | 'channel';
  /** Override saved at this scope; undefined inherits. */
  value: SpaceNotifyLevel | undefined;
  /** What this scope gets without its own override. */
  inherited: ResolvedSpaceNotifyLevel;
  /** Display name of the category inherited from (`inherited.source === 'category'`). */
  inheritedCategoryName?: string;
  onLevelChange: (level: SpaceNotifyLevel | null) => void;
  /** Space scope only: on/off alerts listed under the levels. */
  toggles?: ReadonlyArray<{
    toggle: SpaceNotifyToggle;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
  }>;
  /** Space scope only: clears every override saved for the Space. */
  onReset?: () => void;
  resetDisabled?: boolean;
}

export function NotificationSettingsSubmenu({
  scope,
  value,
  inherited,
  inheritedCategoryName,
  onLevelChange,
  toggles = [],
  onReset,
  resetDisabled = false,
}: NotificationSettingsSubmenuProps) {
  const { t } = useTranslation();
  const { open, onOpenChange, hoverProps } = useHoverSubmenu();
  const levelLabel = (level: SpaceNotifyLevel) =>
    t(`account.settings.notifications.spaces.levels.${level}`);

  const inheritedLevel = levelLabel(inherited.level);
  const inheritLabel =
    scope === 'space'
      ? t('account.settings.notifications.spaces.menu.useDefault', { level: inheritedLevel })
      : t('account.settings.notifications.spaces.menu.inheritFrom', {
          parent:
            inherited.source === 'category'
              ? (inheritedCategoryName ?? t('account.settings.notifications.spaces.menu.parentSpace'))
              : inherited.source === 'space'
                ? t('account.settings.notifications.spaces.menu.parentSpace')
                : t('account.settings.notifications.spaces.menu.parentDefault'),
          level: inheritedLevel,
        });

  const options: Array<{ value: string; label: string }> = [
    { value: INHERIT, label: inheritLabel },
    ...SPACE_NOTIFY_LEVELS.map((level) => ({ value: level, label: levelLabel(level) })),
  ];

  return (
    <Menu.Root
      open={open}
      onOpenChange={onOpenChange}
      positioning={{ placement: 'right-start', gutter: 0 }}
      onSelect={(details) => {
        if (details.value === RESET) onReset?.();
      }}
    >
      <Menu.TriggerItem className="conversation-context-menu-item" {...hoverProps}>
        <Icon name="bell" className="conversation-context-menu-item-icon" />
        {t('account.settings.notifications.spaces.menu.title')}
        <Icon name="chevronRight" className="conversation-context-menu-item-chevron" fixedWidth />
      </Menu.TriggerItem>
      <Portal>
        <Menu.Positioner className="notification-settings-submenu-positioner" {...hoverProps}>
          <Menu.Content className="conversation-context-menu notification-settings-submenu">
            <Menu.RadioItemGroup
              value={value ?? INHERIT}
              onValueChange={(details) => {
                if (details.value === INHERIT) onLevelChange(null);
                else if (isSpaceNotifyLevel(details.value)) onLevelChange(details.value);
              }}
            >
              {options.map((option) => (
                <Menu.RadioItem
                  key={option.value}
                  value={option.value}
                  className="conversation-context-menu-item notification-settings-submenu-option"
                >
                  <Menu.ItemIndicator className="notification-settings-submenu-check">
                    <Icon name="check" fixedWidth />
                  </Menu.ItemIndicator>
                  <Menu.ItemText>{option.label}</Menu.ItemText>
                </Menu.RadioItem>
              ))}
            </Menu.RadioItemGroup>

            {(toggles.length > 0 || onReset) && <Menu.Separator className="conversation-context-menu-separator" />}

            {toggles.map((item) => (
              <Menu.CheckboxItem
                key={item.toggle}
                value={TOGGLE_ITEM_VALUES[item.toggle]}
                checked={item.checked}
                onCheckedChange={item.onCheckedChange}
                closeOnSelect={false}
                className="conversation-context-menu-item notification-settings-submenu-option notification-settings-submenu-toggle"
              >
                <span className="notification-settings-submenu-check" aria-hidden />
                <Menu.ItemText className="notification-settings-submenu-toggle-label">
                  {t(`account.settings.notifications.spaces.menu.${item.toggle}`)}
                </Menu.ItemText>
                {/* Same track/thumb as the app's Ark switches; the menu item owns the checked state. */}
                <span
                  className="sidebar-filter-switch-control"
                  data-state={item.checked ? 'checked' : 'unchecked'}
                  aria-hidden
                >
                  <span className="sidebar-filter-switch-thumb" />
                </span>
              </Menu.CheckboxItem>
            ))}

            {onReset && (
              <Menu.Item value={RESET} disabled={resetDisabled} className="conversation-context-menu-item">
                {t('account.settings.notifications.spaces.menu.reset')}
              </Menu.Item>
            )}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

/** Patch that clears every override saved for a Space. */
export function spaceNotificationsResetPatch(
  overrides: SpaceNotificationOverrides | undefined,
): SpaceNotificationOverridesPatch {
  const patch: SpaceNotificationOverridesPatch = { level: null };
  for (const toggle of SPACE_NOTIFY_TOGGLES) patch[toggle] = null;
  const categories = Object.keys(overrides?.categories ?? {});
  const channels = Object.keys(overrides?.channels ?? {});
  if (categories.length) patch.categories = Object.fromEntries(categories.map((id) => [id, null]));
  if (channels.length) patch.channels = Object.fromEntries(channels.map((id) => [id, null]));
  return patch;
}

/** Space-level submenu (sidebar Space rows). Hidden outside the authenticated shell. */
export function SpaceNotificationsSubmenu({ spaceId }: { spaceId: string }) {
  const settings = useSpaceNotificationSettings();
  if (!settings.available) return null;
  const overrides = settings.overridesFor(spaceId);
  const save = (patch: SpaceNotificationOverridesPatch) => void settings.setSpaceNotifications(spaceId, patch);
  return (
    <NotificationSettingsSubmenu
      scope="space"
      value={overrides?.level}
      inherited={{ level: settings.defaults.level, source: 'default' }}
      onLevelChange={(level) => save({ level })}
      toggles={SPACE_NOTIFY_TOGGLES.map((toggle) => ({
        toggle,
        checked: settings.resolveToggle(spaceId, toggle),
        onCheckedChange: (checked: boolean) => save({ [toggle]: checked }),
      }))}
      onReset={() => save(spaceNotificationsResetPatch(overrides))}
      resetDisabled={!overrides}
    />
  );
}

export interface SpaceScopeNotificationsSubmenuProps {
  spaceId: string;
  scope: 'category' | 'channel';
  /** Category or channel id. */
  id: string;
  /** Parent category of the channel, or of the category itself. */
  parentCategoryId: string | null | undefined;
  categories: ReadonlyArray<{ id: string; parentCategoryId?: string | null }>;
  categoryName: (categoryId: string) => string;
}

/** Category / channel submenu in the Space sidebar. */
export function SpaceScopeNotificationsSubmenu({
  spaceId,
  scope,
  id,
  parentCategoryId,
  categories,
  categoryName,
}: SpaceScopeNotificationsSubmenuProps) {
  const settings = useSpaceNotificationSettings();
  if (!settings.available) return null;
  const overrides = settings.overridesFor(spaceId);
  const value = scope === 'channel' ? overrides?.channels?.[id] : overrides?.categories?.[id];
  const inherited = settings.resolveLevel(spaceId, {
    categoryPath: categoryAncestorPath(parentCategoryId, categories),
  });
  return (
    <NotificationSettingsSubmenu
      scope={scope}
      value={value}
      inherited={inherited}
      inheritedCategoryName={inherited.sourceId ? categoryName(inherited.sourceId) : undefined}
      onLevelChange={(level) =>
        void settings.setSpaceNotifications(
          spaceId,
          scope === 'channel' ? { channels: { [id]: level } } : { categories: { [id]: level } },
        )
      }
    />
  );
}
